/**
 * Which of an enum picker's listed values a filter's picked values stand for.
 *
 * A filter read back from SQL carries the text the shared temporal policy
 * (`sqlTemporalLiteral`) wrote, not the value the user picked: an Oracle
 * `TO_DATE('2024-01-15 00:00:00', …)` reads back as `2024-01-15T00:00:00`
 * (`parseSqlTemporalLiteral`), SQL Server's `'2024-01-15T00:00:00'` as itself,
 * while the picker lists the sampled value (`2024-01-15T00:00:00.000Z`). Two
 * values are the same pick when they write the same SQL literal for the
 * column — so the reopened chip shows the listed days checked, and applying
 * it again writes the same SQL. An exact match always wins; a value with no
 * listed twin (typed ad hoc) stays as it is. Engines that keep the value as
 * written (PostgreSQL, MySQL, SQLite) never need the mapping.
 */

import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import { sqlTemporalLiteral } from '../../../../../core/utils/sqlTemporalLiteral';

export function matchPickedToListed(
  picked: readonly string[],
  listed: readonly string[],
  engine: DatabaseEngineId,
  dataType: string | undefined,
): readonly string[] {
  if (picked.length === 0 || listed.length === 0 || !dataType) return picked;
  const listedSet = new Set(listed);
  if (picked.every((p) => listedSet.has(p))) return picked;
  const byLiteral = new Map<string, string>();
  for (const v of listed) {
    const literal = sqlTemporalLiteral(v, engine, dataType);
    if (literal !== undefined && !byLiteral.has(literal)) byLiteral.set(literal, v);
  }
  const mapped = picked.map((p) => {
    if (listedSet.has(p)) return p;
    const literal = sqlTemporalLiteral(p, engine, dataType);
    return (literal !== undefined && byLiteral.get(literal)) || p;
  });
  // A value and its read-back twin are one pick.
  const out = [...new Set(mapped)];
  return out.length === picked.length && out.every((v, i) => v === picked[i]) ? picked : out;
}
